import { PageSize } from '@/constant';
import {
	GetWorkerFranchiseAssignmentListParams,
	GetWorkerFranchiseListParams,
	GetWorkerFranchiseLodgingListParams,
	GetWorkerFranchiseProgramListParams,
	IFranchiseItem,
	IFranchiseListItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';

import { IPayloadGetWorkerApplicantDetailRequest, RootState } from '..';
import { storage } from '../store.persist';
import {
	formatWorkerFranchiseDetail,
	formatWorkerFranchiseList,
} from './worker-franchise.helper';
import {
	IPayloadApproveWorkerFranchiseProgramRequest,
	IPayloadCreateWorkerFranchiseLodgingRequest,
	IPayloadCreateWorkerFranchiseProgramRequest,
	IPayloadCreateWorkerFranchiseRequest,
	IPayloadDeleteWorkerFranchiseAssignmentRequest,
	IPayloadDeleteWorkerFranchiseLodgingRequest,
	IPayloadDeleteWorkerFranchiseProgramRequest,
	IPayloadDeleteWorkerFranchiseRecommenderRequest,
	IPayloadDeleteWorkerFranchiseRequest,
	IPayloadGetWorkerFranchiseAssignmentListRequest,
	IPayloadGetWorkerFranchiseAssignmentListSuccess,
	IPayloadGetWorkerFranchiseDetailRequest,
	IPayloadGetWorkerFranchiseDetailSuccess,
	IPayloadGetWorkerFranchiseExcelListRequest,
	IPayloadGetWorkerFranchiseListRequest,
	IPayloadGetWorkerFranchiseListSuccess,
	IPayloadGetWorkerFranchiseLodgingListRequest,
	IPayloadGetWorkerFranchiseLodgingListSuccess,
	IPayloadGetWorkerFranchiseProgramListRequest,
	IPayloadGetWorkerFranchiseProgramListSuccess,
	IPayloadGetWorkerFranchiseRecommenderInfoSuccess,
	IPayloadRejectWorkerFranchiseProgramRequest,
	IWorkerFranchiseAssignmentDetailStore,
	IWorkerFranchiseDetailStore,
	IWorkerFranchiseListItemStore,
	IWorkerFranchiseLodgingDetailStore,
	IWorkerFranchiseProgramDetailStore,
	IWorkerFranchiseRecommenderStore,
	TFranchiseUploadFailedDetailStore,
	TPayloadUploadExcelWorkerFranchiseRequest,
	TPayloadUploadExcelWorkerFranchiseSuccess,
} from './worker-franchise.type';

const InitParams: GetWorkerFranchiseListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
	employerName: '',
	districtId: '',
};

const InitAssignmentParams: GetWorkerFranchiseAssignmentListParams = {
	page: 1,
	pageSize: PageSize.S,
	programId: '',
	applicantId: '',
};

const InitLodgingParams: GetWorkerFranchiseLodgingListParams = {
	page: 1,
	pageSize: PageSize.S,
};

const InitProgramParams: GetWorkerFranchiseProgramListParams = {
	page: 1,
	pageSize: PageSize.S,
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerFranchiseList: [] as IWorkerFranchiseListItemStore[],
	page: 1,
	total: 0,
	params: InitParams,
	workerFranchiseDetail: {} as IWorkerFranchiseDetailStore,

	assignmentParams: InitAssignmentParams,
	assignmentList: [] as IWorkerFranchiseAssignmentDetailStore[],
	totalAssignmentList: 0,

	lodgingParams: InitLodgingParams,
	lodgingList: [] as IWorkerFranchiseLodgingDetailStore[],
	totalLodgingList: 0,

	programParams: InitProgramParams,
	programList: [] as IWorkerFranchiseProgramDetailStore[],
	totalProgramList: 0,

	workerFranchiseRecommenderInfo: [] as IWorkerFranchiseRecommenderStore[],

	uploadExcelWorkerFranchiseFailedList:
		[] as TFranchiseUploadFailedDetailStore[],
	uploadExcelWorkerFranchiseResult: {
		successCount: 0,
		failCount: 0,
	} as {
		successCount: number;
		failCount: number;
	},

	isBrowserClosing: false,
};

const workerFranchiseSlice = createSlice({
	initialState,
	name: 'workerFranchise',
	reducers: {
		/**---- GET WORKER_FRANCHISE LIST START ---- */
		getWorkerFranchiseListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getWorkerFranchiseListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseListSuccess>
		) => {
			const { content, page, total } = action.payload;
			state.fetching = false;
			state.workerFranchiseList = content;
			state.page = page;
			state.total = total;
		},
		getWorkerFranchiseListFailure: (state) => {
			state.fetching = false;
		},

		getWorkerFranchiseExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerFranchiseExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerFranchiseExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER_FRANCHISE LIST END ---- */

		/**---- CREATE WORKER Franchise START ---- */
		createWorkerFranchiseRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerFranchiseRequest>
		) => {
			state.fetching = true;
		},
		createWorkerFranchiseSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerFranchiseFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER Franchise END ---- */

		/**---- GET WORKER_FRANCHISE DETAIL START ---- */
		getWorkerFranchiseDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerFranchiseDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseDetailSuccess>
		) => {
			const { workerFranchiseDetail } = action.payload;
			state.fetching = false;
			state.workerFranchiseDetail = workerFranchiseDetail;
		},
		getWorkerFranchiseDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER_FRANCHISE DETAIL END ---- */

		/**---- UPDATE WORKER WORKER_FRANCHISE START ---- */
		updateWorkerFranchiseSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IFranchiseListItem>>
		) => {
			const { data } = action.payload;
			const workerFranchiseList = [...state.workerFranchiseList];
			state.workerFranchiseList = formatWorkerFranchiseList({
				input: mergeListItemUpdated<
					IWorkerFranchiseListItemStore,
					IFranchiseListItem
				>(workerFranchiseList, data),
			});
		},
		updateWorkerFranchiseDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IFranchiseItem>>
		) => {
			const { data } = action.payload;
			const workerFranchiseDetail = { ...state.workerFranchiseDetail };
			state.workerFranchiseDetail = formatWorkerFranchiseDetail(
				mergeItemUpdated<IWorkerFranchiseDetailStore, IFranchiseItem>(
					workerFranchiseDetail,
					data
				)
			);
		},
		/**---- UPDATE WORKER WORKER_FRANCHISE END ---- */

		/**---- DELETE WORKER FRANCHISE START ---- */
		deleteWorkerFranchiseRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerFranchiseRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerFranchiseSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerFranchiseFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER FRANCHISE START ---- */

		/**---- GET WORKER FRANCHISE ASSIGNMENT LIST START ---- */
		getWorkerFranchiseAssignmentListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseAssignmentListRequest>
		) => {
			const { params } = action.payload;
			return {
				...state,
				fetching: true,
				assignmentParams: params,
			};
		},
		getWorkerFranchiseAssignmentListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseAssignmentListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.assignmentList = content;
			state.totalAssignmentList = total;
		},
		getWorkerFranchiseAssignmentListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.assignmentList = [];
		},
		/**---- GET WORKER FRANCHISE ASSIGNMENT LIST END ---- */

		/**---- DELETE WORKER FRANCHISE ASSIGNMENT START ---- */
		deleteWorkerFranchiseAssignmentRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerFranchiseAssignmentRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerFranchiseAssignmentSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerFranchiseAssignmentFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER FRANCHISE ASSIGNMENT END ---- */

		/**---- GET WORKER FRANCHISE LODGING LIST START ---- */
		getWorkerFranchiseLodgingListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseLodgingListRequest>
		) => {
			const { params } = action.payload;
			return {
				...state,
				fetching: true,
				lodgingParams: params,
			};
		},
		getWorkerFranchiseLodgingListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseLodgingListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.lodgingList = content;
			state.totalLodgingList = total;
		},
		getWorkerFranchiseLodgingListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.lodgingList = [];
		},
		/**---- GET WORKER FRANCHISE LODGING LIST END ---- */

		/**---- GET WORKER FRANCHISE RECOMMENDER INFO LIST START ---- */
		getWorkerFranchiseRecommenderInfoRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerFranchiseRecommenderInfoSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseRecommenderInfoSuccess>
		) => {
			const { workerFranchiseRecommenderInfo } = action.payload;
			state.fetching = false;
			state.workerFranchiseRecommenderInfo = workerFranchiseRecommenderInfo;
		},
		getWorkerFranchiseRecommenderInfoFailure: (state) => {
			state.fetching = false;
			state.workerFranchiseRecommenderInfo =
				[] as IWorkerFranchiseRecommenderStore[];
		},
		/**---- GET WORKER FRANCHISE RECOMMENDER INFO LIST END ---- */

		/**---- DELETE WORKER FRANCHISE RECOMMENDER START ---- */
		deleteWorkerFranchiseRecommenderRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerFranchiseRecommenderRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerFranchiseRecommenderSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerFranchiseRecommenderFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER FRANCHISE RECOMMENDER END ---- */

		/**---- DELETE WORKER FRANCHISE LODGING START ---- */
		deleteWorkerFranchiseLodgingRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerFranchiseLodgingRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerFranchiseLodgingSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerFranchiseLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER FRANCHISE LODGING END ---- */

		/**---- CREATE WORKER FRANCHISE LODGING START ---- */
		createWorkerFranchiseLodgingRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerFranchiseLodgingRequest>
		) => {
			state.fetching = true;
		},
		createWorkerFranchiseLodgingSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerFranchiseLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER FRANCHISE LODGING END ---- */

		/**---- GET WORKER FRANCHISE PROGRAM LIST START ---- */
		getWorkerFranchiseProgramListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseProgramListRequest>
		) => {
			const { params } = action.payload;
			state.fetching = true;
			state.programParams = params;
		},
		getWorkerFranchiseProgramListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerFranchiseProgramListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.programList = content;
			state.totalProgramList = total;
		},
		getWorkerFranchiseProgramListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.programList = [];
		},
		/**---- GET WORKER FRANCHISE PROGRAM LIST END ---- */

		/**---- DELETE WORKER FRANCHISE PROGRAM START ---- */
		deleteWorkerFranchiseProgramRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerFranchiseProgramRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerFranchiseProgramSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerFranchiseProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER FRANCHISE PROGRAM END ---- */

		/**---- CREATE WORKER FRANCHISE PROGRAM START ---- */
		createWorkerFranchiseProgramRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerFranchiseProgramRequest>
		) => {
			state.fetching = true;
		},
		createWorkerFranchiseProgramSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerFranchiseProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER FRANCHISE PROGRAM END ---- */

		/**---- UPLOAD EXCEL WORKER FRANCHISE START ---- */

		uploadExcelWorkerFranchiseRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelWorkerFranchiseRequest>
		) => {
			state.fetching = true;

			state.uploadExcelWorkerFranchiseFailedList = [];
			state.uploadExcelWorkerFranchiseResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelWorkerFranchiseSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelWorkerFranchiseSuccess>
		) => {
			state.fetching = false;
			state.uploadExcelWorkerFranchiseFailedList = action.payload.failedList;
			state.uploadExcelWorkerFranchiseResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
		},
		uploadExcelWorkerFranchiseFailure: (state) => {
			state.fetching = false;
			state.uploadExcelWorkerFranchiseResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		/**---- UPLOAD EXCEL WORKER FRANCHISE END ---- */

		/**---- APPROVE WORKER FRANCHISE PROGRAM START ---- */
		approveWorkerFranchiseProgramRequest: (
			state,
			action: PayloadAction<IPayloadApproveWorkerFranchiseProgramRequest>
		) => {
			state.fetching = true;
		},
		approveWorkerFranchiseProgramSuccess: (state) => {
			state.fetching = false;
		},
		approveWorkerFranchiseProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- APPROVE WORKER FRANCHISE PROGRAM END ---- */

		/**---- REJECT WORKER FRANCHISE PROGRAM START ---- */
		rejectWorkerFranchiseProgramRequest: (
			state,
			action: PayloadAction<IPayloadRejectWorkerFranchiseProgramRequest>
		) => {
			state.fetching = true;
		},
		rejectWorkerFranchiseProgramSuccess: (state) => {
			state.fetching = false;
		},
		rejectWorkerFranchiseProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- REJECT WORKER FRANCHISE PROGRAM END ---- */

		resetWorkerFranchiseDetail: (state) => {
			state.workerFranchiseDetail = {} as IWorkerFranchiseDetailStore;
		},
	},
});

export const WorkerFranchiseSelectors = {
	selectFetching: (state: RootState) => state.workerFranchise.fetching,
	selectParams: (state: RootState) => state.workerFranchise.params,
	selectTotal: (state: RootState) => state.workerFranchise.total,
	selectList: (state: RootState) => state.workerFranchise.workerFranchiseList,
	selectDetail: (state: RootState) =>
		state.workerFranchise.workerFranchiseDetail,

	selectAssignmentTotal: (state: RootState) =>
		state.workerFranchise.totalAssignmentList,
	selectAssignmentListParams: (state: RootState) =>
		state.workerFranchise.assignmentParams,
	selectAssignmentList: (state: RootState) =>
		state.workerFranchise.assignmentList,

	selectLodgingTotal: (state: RootState) =>
		state.workerFranchise.totalLodgingList,
	selectLodgingListParams: (state: RootState) =>
		state.workerFranchise.lodgingParams,
	selectLodgingList: (state: RootState) => state.workerFranchise.lodgingList,

	selectProgramTotal: (state: RootState) =>
		state.workerFranchise.totalProgramList,
	selectProgramListParams: (state: RootState) =>
		state.workerFranchise.programParams,
	selectProgramList: (state: RootState) => state.workerFranchise.programList,

	selectUploadExcelWorkerFranchiseFailedList: (state: RootState) =>
		state.workerFranchise.uploadExcelWorkerFranchiseFailedList,
	selectUploadExcelWorkerFranchiseResult: (state: RootState) =>
		state.workerFranchise.uploadExcelWorkerFranchiseResult,

	selectRecommenderInfo: (state: RootState) =>
		state.workerFranchise.workerFranchiseRecommenderInfo,
};

export const WorkerFranchiseActions = workerFranchiseSlice.actions;
export const WORKER_FRANCHISE_PERSIST_KEY = 'workerFranchisePersist';
export const WorkerFranchiseReducer = persistReducer(
	{
		key: WORKER_FRANCHISE_PERSIST_KEY,
		storage: storage,
		whitelist: ['workerFranchiseDetail'],
	},
	workerFranchiseSlice.reducer
);
